import { useCallback, useEffect, useRef, useState } from "react";
import { adminApi } from "../apiAdmin";

// One admin read owned by a tab (the Dashboard, Requests, Revenue and
// Trending tabs each ask the server for exactly what they show).
//
//   data    the answer to the CURRENT url — an answer to an older choice is
//           never shown under a newer one; null while it loads
//   latest  the last answer to any url, for a list that keeps showing while
//           a new filter loads (the Fans tab does the same)
//   failed  the current url's read failed; `retry` reads it again
//
// A new `refreshKey` reads the same url again (the header's ↻ Refresh).
// `parse` turns whatever came back into the shape the tab draws, so a
// malformed answer renders as empty rather than crashing the tab.
export function useAdminRead<T>(url: string, parse: (raw: unknown) => T, refreshKey = 0) {
  const [answer, setAnswer] = useState<{ url: string; data: T } | null>(null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const call = useRef(0);
  const parseRef = useRef(parse);
  parseRef.current = parse;

  const load = useCallback(() => {
    const mine = ++call.current;
    setFailedUrl(null);
    adminApi.get(url).then(
      (r) => {
        if (mine === call.current) setAnswer({ url, data: parseRef.current(r.data) });
      },
      () => {
        if (mine === call.current) setFailedUrl(url);
      }
    );
  }, [url]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return {
    data: answer && answer.url === url ? answer.data : null,
    latest: answer ? answer.data : null,
    failed: failedUrl === url,
    retry: load,
  };
}
